import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Check, Edit3, User, Building2 } from 'lucide-react';

export function EditLeadModal({ isOpen, onClose, lead, onUpdateLead, showToast }) {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    channel: 'line',
    propertyType: 'Office',
    space: '',
    budget: '',
    status: 'qualified',
    assignedTo: 'TBS Sales Team'
  });

  const [isCustomSpace, setIsCustomSpace] = useState(false);
  const [isCustomBudget, setIsCustomBudget] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        company: lead.company || '',
        email: lead.email || '',
        phone: lead.phone || '',
        channel: lead.channel || 'line',
        propertyType: lead.propertyType || 'Office',
        space: lead.space || '120 sqm',
        budget: lead.budget || '฿85,000 / mo',
        status: lead.status || 'qualified',
        assignedTo: lead.assignedTo || 'TBS Sales Team'
      });
    }
  }, [lead]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!lead || !formData.name || !formData.company) return;

    onUpdateLead(lead.id, formData);
    if (showToast) showToast(`Updated lead: ${formData.company}`, 'success');
    onClose();
  };

  const propertyOptions = [
    { label: 'Office Space', val: 'Office', icon: '🏢' },
    { label: 'Retail Shop', val: 'Retail Shop', icon: '🛍️' },
    { label: 'Warehouse', val: 'Warehouse', icon: '🏭' },
    { label: 'Villa / Condo', val: 'Villa / Condo', icon: '🏡' }
  ];

  const spaceOptions = ['50 sqm', '80 sqm', '120 sqm', '250 sqm', '500 sqm'];
  const budgetOptions = ['฿35,000 / mo', '฿65,000 / mo', '฿85,000 / mo', '฿150,000 / mo', '฿300,000 / mo'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Lead Details" maxWidth="560px">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {/* Section 1: Contact Information */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#09090B', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <User size={13} color="#2cabe1" />
            <span>Contact Details</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px' }}>
                Contact Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px' }}>
                Company Name <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="text"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px' }}>Phone Number</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px' }}>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ height: '1px', backgroundColor: '#E4E4E7' }} />

        {/* Section 2: Property Requirements */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#09090B', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Building2 size={13} color="#2cabe1" />
            <span>Property & Pipeline Status</span>
          </div>

          {/* Property Type Grid Selector */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
              Property Type
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {propertyOptions.map((p) => {
                const isSelected = formData.propertyType === p.val;
                return (
                  <button
                    key={p.val}
                    type="button"
                    onClick={() => setFormData({ ...formData, propertyType: p.val })}
                    style={{
                      padding: '8px 6px',
                      borderRadius: '8px',
                      border: isSelected ? '2px solid #2cabe1' : '1px solid #E4E4E7',
                      backgroundColor: isSelected ? '#EBF8FC' : '#FFFFFF',
                      color: isSelected ? '#1e92c4' : '#374151',
                      fontSize: '12px',
                      fontWeight: isSelected ? 700 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{p.icon}</span>
                    <span>{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Space Required */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Space Required</label>
              <button
                type="button"
                onClick={() => setIsCustomSpace(!isCustomSpace)}
                style={{ fontSize: '11px', color: '#2cabe1', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {isCustomSpace ? '• Select Presets' : '• Type Custom'}
              </button>
            </div>

            {!isCustomSpace ? (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {spaceOptions.map((sp) => {
                  const isSelected = formData.space === sp;
                  return (
                    <button
                      key={sp}
                      type="button"
                      onClick={() => setFormData({ ...formData, space: sp })}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: isSelected ? '2px solid #2cabe1' : '1px solid #E4E4E7',
                        backgroundColor: isSelected ? '#2cabe1' : '#FAFAFC',
                        color: isSelected ? '#FFFFFF' : '#374151',
                        fontSize: '12px',
                        fontWeight: isSelected ? 700 : 500,
                        cursor: 'pointer'
                      }}
                    >
                      {sp}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                type="text"
                value={formData.space}
                onChange={(e) => setFormData({ ...formData, space: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            )}
          </div>

          {/* Monthly Budget */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Monthly Budget</label>
              <button
                type="button"
                onClick={() => setIsCustomBudget(!isCustomBudget)}
                style={{ fontSize: '11px', color: '#83c341', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {isCustomBudget ? '• Select Presets' : '• Type Custom'}
              </button>
            </div>

            {!isCustomBudget ? (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {budgetOptions.map((bg) => {
                  const isSelected = formData.budget === bg;
                  return (
                    <button
                      key={bg}
                      type="button"
                      onClick={() => setFormData({ ...formData, budget: bg })}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: isSelected ? '2px solid #83c341' : '1px solid #E4E4E7',
                        backgroundColor: isSelected ? '#83c341' : '#FAFAFC',
                        color: isSelected ? '#FFFFFF' : '#374151',
                        fontSize: '12px',
                        fontWeight: isSelected ? 700 : 500,
                        cursor: 'pointer'
                      }}
                    >
                      {bg}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                type="text"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              />
            )}
          </div>

          {/* Status & Assignee */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Lead Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              >
                <option value="new">NEW</option>
                <option value="qualified">QUALIFIED</option>
                <option value="contacted">CONTACTED</option>
                <option value="site_visit">SITE VISIT</option>
                <option value="proposal">PROPOSAL</option>
                <option value="leased">LEASED</option>
                <option value="lost">LOST</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Assigned Team</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                style={{ width: '100%', height: '38px', borderRadius: '6px' }}
              >
                <option value="TBS Sales Team">TBS Sales Team</option>
                <option value="TBS Specialist">TBS Specialist</option>
                <option value="TBS Account Executive">TBS Account Executive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px', paddingTop: '16px', borderTop: '1px solid #E4E4E7' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: '38px',
              padding: '0 18px',
              borderRadius: '6px',
              border: '1px solid #D4D4D8',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              height: '38px',
              padding: '0 22px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2cabe1',
              color: '#FFFFFF',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Check size={16} />
            <span>Update Lead</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}
